import type { components, Practice } from "@brandex/api-client";
import { formatDateTime, type MessageKey } from "@brandex/i18n";
import { ACCENT_KEYS, accentHex, Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, ChoiceChips, Field, Spinner, Textarea, cn, type AccentKey } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImageUp, Sparkles, Trash2 } from "lucide-react";
import { useId, useRef, useState, type ReactNode } from "react";

import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";
import { SHOOT_TYPES } from "../onboarding/draft";
import { useCurrentOrg } from "../client-scope";
import { keys, mediaUrl, usePractice } from "../queries";

type Brand = Practice["profile"]["brand"];
type Slot = "photo" | "logo" | "clinic_logo" | "favicon";
type TemplateRequest = components["schemas"]["TemplateRequestDto"];
type Design = NonNullable<Practice["profile"]["custom_design"]>;

type Tone = NonNullable<Brand["caption_tone"]>;
type Template = NonNullable<Brand["template"]>;

/** Built-in templates every doctor can choose (`custom` appears once BrandEx delivers one). */
export const TEMPLATES: readonly Exclude<Template, "custom">[] = ["modern", "classic", "minimal", "editorial", "clinical", "elegant", "bold", "warm", "spotlight"];
export const CAPTION_TONES: readonly Tone[] = ["warm_simple", "professional_formal", "educational"];
/** Accepted upload types; the server checks the bytes, this only filters the picker. */
const ACCEPT = "image/png,image/jpeg,image/webp";
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Brand kit (F-15): accent colour, website template (nine built in, plus the
 * practice's own once BrandEx delivers it), photo, logos and favicon,
 * preferred shoot type and caption tone, and custom template requests.
 * Images are re-encoded by the server, which also strips location data from
 * phone photos.
 */
export function BrandKitView() {
  const t = useT();
  const { nav } = useBrandex();
  const orgs = useCurrentOrg();
  const orgId = orgs.orgId;
  const practice = usePractice(orgId);
  if (orgs.isPending || (orgId !== undefined && practice.isPending)) return <Spinner label={t("common.loading")} />;
  if (orgs.isError || practice.isError) return <Alert tone="error">{errorMessage(orgs.error ?? practice.error, t)}</Alert>;
  if (orgId === undefined || !practice.data) {
    return (
      <div className="mx-auto grid max-w-3xl gap-4">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("brand.title")}</h1>
        <Alert tone="info">{t("website.state.needs_onboarding")}</Alert>
        <div><Button onClick={() => nav.navigate("/onboarding")}>{t("onboarding.continue")}</Button></div>
      </div>
    );
  }
  return <BrandKit orgId={orgId} practice={practice.data} />;
}

function BrandKit({ orgId, practice }: { orgId: string; practice: Practice }) {
  const t = useT();
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [brand, setBrand] = useState<Brand>(practice.profile.brand);
  const save = useMutation({
    mutationFn: async (b: Brand) => {
      const { data } = await api.http.PUT("/v1/orgs/{org_id}/brand", { params: { path: { org_id: orgId } }, body: b });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.practice(orgId), data);
      setBrand(data.profile.brand);
    },
  });
  const change = (patch: Partial<Brand>) => {
    save.reset();
    setBrand((b) => ({ ...b, ...patch }));
  };
  const dirty = JSON.stringify(brand) !== JSON.stringify(practice.profile.brand);
  return (
    <div className="mx-auto grid max-w-4xl gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("brand.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("brand.subtitle")}</p>
      </header>
      <Preview practice={practice} accent={brand.accent} />

      <Card>
        <CardHeader>
          <CardTitle>{t("onboarding.accent")}</CardTitle>
          <CardDescription>{t("brand.accentHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <ChoiceChips
            label={t("onboarding.accent")}
            value={brand.accent}
            onChange={(accent: AccentKey) => change({ accent })}
            options={ACCENT_KEYS.map((k) => ({
              value: k,
              ariaLabel: t(`accent.${k}` as MessageKey),
              label: (
                <>
                  <span aria-hidden className="h-4 w-4 rounded-full ring-2 ring-white/70" style={{ background: accentHex(k) }} />
                  <span>{t(`accent.${k}` as MessageKey)}</span>
                </>
              ),
            }))}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("brand.website")}</CardTitle>
          <CardDescription>{t("brand.websiteHint")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5">
          <div role="radiogroup" aria-label={t("brand.template")} className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            {practice.profile.custom_design ? (
              <CustomTemplateCard design={practice.profile.custom_design} on={brand.template === "custom"} onSelect={() => change({ template: "custom" })} />
            ) : null}
            {TEMPLATES.map((k) => {
              const on = (brand.template ?? "modern") === k;
              return (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => change({ template: k })}
                  className={cn("grid gap-2 rounded-[var(--radius-lg)] border p-2 text-left transition hover:border-primary", on ? "border-primary ring-2 ring-primary/40" : "border-border")}
                >
                  <TemplateThumb template={k} accent={accentHex(brand.accent)} />
                  <span className="px-1 text-sm font-semibold">{t(`brand.template.${k}` as MessageKey)}</span>
                  <span className="px-1 pb-1 text-xs text-muted-foreground">{t(`brand.template.${k}.hint` as MessageKey)}</span>
                </button>
              );
            })}
          </div>
          <label className="flex items-start gap-3 rounded-[var(--radius-lg)] border border-border p-4 text-sm">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--color-primary)]" checked={brand.show_posts ?? true} onChange={(e) => change({ show_posts: e.target.checked })} />
            <span className="grid gap-0.5">
              <span className="font-medium">{t("brand.showPosts")}</span>
              <span className="text-muted-foreground">{t("brand.showPostsHint")}</span>
            </span>
          </label>
          <p className="text-xs text-muted-foreground">{t("brand.websiteDelay")}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("brand.images")}</CardTitle>
          <CardDescription>{t("brand.imagesHint")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <ImageSlot orgId={orgId} practice={practice} slot="photo" />
          <ImageSlot orgId={orgId} practice={practice} slot="logo" />
          <ImageSlot orgId={orgId} practice={practice} slot="clinic_logo" />
          <ImageSlot orgId={orgId} practice={practice} slot="favicon" />
        </CardContent>
      </Card>

      <CustomTemplateRequests orgId={orgId} design={practice.profile.custom_design ?? null} />

      <Card>
        <CardHeader>
          <CardTitle>{t("brand.content")}</CardTitle>
          <CardDescription>{t("brand.toneHint")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5">
          <Labelled label={t("onboarding.shootType")}>
            <ChoiceChips
              label={t("onboarding.shootType")}
              value={brand.shoot_type}
              onChange={(shoot_type) => change({ shoot_type })}
              options={SHOOT_TYPES.map((s) => ({ value: s, label: t(`shoot.${s}` as MessageKey) }))}
            />
          </Labelled>
          <Labelled label={t("brand.tone")}>
            <ChoiceChips
              label={t("brand.tone")}
              value={brand.caption_tone ?? "warm_simple"}
              onChange={(caption_tone: Tone) => change({ caption_tone })}
              options={CAPTION_TONES.map((v) => ({ value: v, label: t(`brand.tone.${v}` as MessageKey) }))}
            />
          </Labelled>
        </CardContent>
      </Card>

      <div className="grid gap-3">
        {save.isError ? <Alert tone="error">{errorMessage(save.error, t)}</Alert> : null}
        {save.isSuccess ? <Alert tone="success">{t("profile.saved")}</Alert> : null}
        <div><Button disabled={!dirty} loading={save.isPending} onClick={() => save.mutate(brand)}>{t("brand.save")}</Button></div>
      </div>
    </div>
  );
}

/** A miniature of each website layout, drawn in the chosen accent. */
function TemplateThumb({ template, accent }: { template: Exclude<Template, "custom">; accent: string }) {
  const line = (w: string, extra = "") => <span className={cn("block h-1.5 rounded-full bg-slate-300", extra)} style={{ width: w }} />;
  return (
    <span aria-hidden className="block aspect-[16/10] overflow-hidden rounded-md border border-slate-200 bg-white p-2">
      {template === "modern" ? (
        <span className="grid h-full grid-cols-[1.3fr_1fr] gap-2 rounded" style={{ background: `linear-gradient(180deg, ${accent}22, white)` }}>
          <span className="grid content-center gap-1 pl-1">{line("40%", "bg-slate-400")}{line("85%", "h-2.5 bg-slate-700")}{line("70%")}<span className="mt-1 block h-2.5 w-10 rounded" style={{ background: accent }} /></span>
          <span className="m-1 rounded-lg" style={{ background: accent }} />
        </span>
      ) : template === "classic" ? (
        <span className="grid h-full grid-rows-[1.1fr_1fr] gap-1.5">
          <span className="grid place-items-center content-center gap-1 rounded" style={{ background: accent }}>
            <span className="block h-5 w-5 rounded-full border-2 border-white/70 bg-white/30" />
            <span className="block h-1.5 w-16 rounded-full bg-white/80" />
          </span>
          <span className="grid grid-cols-3 gap-1">{[0, 1, 2].map((i) => <span key={i} className="rounded border border-slate-200 bg-slate-50" />)}</span>
        </span>
      ) : template === "minimal" ? (
        <span className="grid h-full content-center gap-1.5 px-2">
          {line("30%", "bg-slate-300")}
          {line("75%", "h-3 bg-slate-800")}
          <span className="block h-0.5 w-10" style={{ background: accent }} />
          {line("90%")}{line("60%")}
        </span>
      ) : template === "editorial" ? (
        <span className="grid h-full grid-cols-3 grid-rows-[auto_1fr] gap-1">
          <span className="col-span-3 block h-3 w-2/3 rounded-full bg-slate-800" />
          <span className="col-span-2 row-span-1 rounded" style={{ background: accent }} />
          <span className="grid gap-1"><span className="rounded bg-slate-200" /><span className="rounded bg-slate-200" /></span>
        </span>
      ) : template === "clinical" ? (
        <span className="grid h-full grid-cols-[1.2fr_1fr] gap-2 bg-slate-50 p-0.5">
          <span className="grid content-center gap-1">
            <span className="block h-4 w-4 rounded" style={{ background: accent }} />
            {line("80%", "h-2.5 bg-slate-700")}{line("60%")}
          </span>
          <span className="grid content-center gap-1 rounded border border-slate-200 bg-white p-1">
            {line("90%", "bg-slate-200")}{line("90%", "bg-slate-200")}<span className="block h-2 rounded" style={{ background: accent }} />
          </span>
        </span>
      ) : template === "elegant" ? (
        <span className="grid h-full grid-cols-[1.3fr_1fr] items-center gap-2 rounded px-1" style={{ background: `linear-gradient(160deg, ${accent}66, #0b1020)` }}>
          <span className="grid gap-1">{line("50%", "bg-white/50")}{line("85%", "h-2.5 bg-white")}{line("30%", "h-px bg-white/70")}</span>
          <span className="mx-auto h-[85%] w-3/4 rounded-t-full border border-white/40 bg-white/20" />
        </span>
      ) : template === "bold" ? (
        <span className="grid h-full grid-cols-[1.4fr_1fr] items-end gap-2 rounded p-1" style={{ background: accent }}>
          <span className="grid gap-1">{line("40%", "bg-white/60")}{line("95%", "h-3.5 bg-white")}{line("70%", "h-3.5 bg-white")}</span>
          <span className="aspect-square rounded-sm border-2 border-white/70 bg-white/25" />
        </span>
      ) : template === "warm" ? (
        <span className="grid h-full grid-cols-[1fr_1.3fr] items-center gap-2 rounded-xl px-1" style={{ background: `linear-gradient(135deg, #fdf3e7, ${accent}22)` }}>
          <span className="mx-auto aspect-square w-4/5 rounded-full border-2 border-white" style={{ background: `${accent}88` }} />
          <span className="grid gap-1">{line("50%", "rounded-full bg-amber-200")}{line("85%", "h-2.5 bg-stone-700")}{line("60%")}</span>
        </span>
      ) : (
        <span className="relative grid h-full items-end overflow-hidden rounded" style={{ background: `linear-gradient(180deg, ${accent}55, #0f172a)` }}>
          <span className="grid gap-1 p-1.5">{line("80%", "h-3 bg-white")}{line("50%", "bg-white/60")}</span>
        </span>
      )}
    </span>
  );
}

/** The practice's own template, once BrandEx has delivered it. */
function CustomTemplateCard({ design, on, onSelect }: { design: Design; on: boolean; onSelect: () => void }) {
  const t = useT();
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onSelect}
      className={cn("col-span-2 grid gap-2 rounded-[var(--radius-lg)] border p-2 text-left transition hover:border-primary md:col-span-1", on ? "border-primary ring-2 ring-primary/40" : "border-border")}
    >
      <span aria-hidden className="relative grid aspect-[16/10] place-items-center overflow-hidden rounded-md border" style={{ background: design.surface, borderColor: `${design.primary}55` }}>
        <span className="grid justify-items-center gap-1.5">
          <Sparkles className="h-5 w-5" style={{ color: design.primary }} />
          <span className="flex gap-1">
            {[design.primary, design.ink, design.surface].map((c) => <span key={c} className="h-3 w-3 rounded-full border border-black/10" style={{ background: c }} />)}
          </span>
        </span>
      </span>
      <span className="px-1 text-sm font-semibold">{t("brand.template.custom", { name: design.name })}</span>
      <span className="px-1 pb-1 text-xs text-muted-foreground">{t("brand.template.custom.hint")}</span>
    </button>
  );
}

/** "/v1/orgs/{org}/template-requests" query key. */
const requestsKey = (org: string) => ["orgs", org, "template-requests"] as const;

/** Links typed one per line → the list the API takes (blank lines dropped). */
export function parseLinks(raw: string): string[] {
  return raw.split(/\s*\n\s*/).map((l) => l.trim()).filter((l) => l !== "");
}

/** Mirrors the server's rules so mistakes show before sending. */
export function linksValid(links: readonly string[]): boolean {
  return links.length <= 5 && links.every((l) => /^https:\/\/[^\s/@]+\.[^\s/@]+(\/\S*)?$/.test(l));
}

/** Ask BrandEx for a custom template, and follow earlier requests. */
function CustomTemplateRequests({ orgId, design }: { orgId: string; design: Design | null }) {
  const t = useT();
  const { api, locale } = useBrandex();
  const qc = useQueryClient();
  const [brief, setBrief] = useState("");
  const [links, setLinks] = useState("");
  const [touched, setTouched] = useState(false);
  const requests = useQuery<TemplateRequest[]>({
    queryKey: requestsKey(orgId),
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/template-requests", { params: { path: { org_id: orgId } } });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const send = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/orgs/{org_id}/template-requests", {
        params: { path: { org_id: orgId } },
        body: { brief: brief.trim(), references: parseLinks(links) },
      });
    },
    onSuccess: () => {
      setBrief("");
      setLinks("");
      setTouched(false);
      return qc.invalidateQueries({ queryKey: requestsKey(orgId) });
    },
  });
  const cancel = useMutation({
    mutationFn: async (id: string) => {
      await api.http.POST("/v1/orgs/{org_id}/template-requests/{request_id}/cancel", { params: { path: { org_id: orgId, request_id: id } } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: requestsKey(orgId) }),
  });
  const open = requests.data?.some((r) => r.status === "open" || r.status === "in_progress") ?? false;
  const briefError = touched && brief.trim().length < 20 ? t("brand.custom.badBrief") : undefined;
  const linksError = touched && !linksValid(parseLinks(links)) ? t("brand.custom.badLinks") : undefined;
  const tone = (s: TemplateRequest["status"]) => (s === "delivered" ? "success" : s === "declined" || s === "cancelled" ? "neutral" : "accent");
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" aria-hidden />{t("brand.custom.title")}</CardTitle>
        <CardDescription>{t("brand.custom.hint")}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        {design ? <Alert tone="success">{t("brand.custom.ready", { name: design.name })}</Alert> : null}
        {send.isSuccess ? <Alert tone="success">{t("brand.custom.sent")}</Alert> : null}
        {open ? (
          <Alert tone="info">{t("brand.custom.openRequest")}</Alert>
        ) : (
          <form
            className="grid gap-4"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              setTouched(true);
              if (brief.trim().length >= 20 && linksValid(parseLinks(links))) send.mutate();
            }}
          >
            <Field label={t("brand.custom.brief")} hint={t("brand.custom.briefHint")} error={briefError}>
              <Textarea rows={4} maxLength={2000} value={brief} onChange={(e) => { send.reset(); setBrief(e.target.value); }} />
            </Field>
            <Field label={t("brand.custom.references")} hint={t("brand.custom.referencesHint")} error={linksError}>
              <Textarea rows={3} value={links} placeholder="https://" onChange={(e) => setLinks(e.target.value)} />
            </Field>
            {send.isError ? <Alert tone="error">{errorMessage(send.error, t)}</Alert> : null}
            <div><Button type="submit" loading={send.isPending}>{t("brand.custom.send")}</Button></div>
          </form>
        )}
        {requests.isError ? <Alert tone="error">{errorMessage(requests.error, t)}</Alert> : null}
        {cancel.isError ? <Alert tone="error">{errorMessage(cancel.error, t)}</Alert> : null}
        {requests.data !== undefined && requests.data.length > 0 ? (
          <section className="grid gap-2" aria-label={t("brand.custom.requests")}>
            <h3 className="text-sm font-semibold">{t("brand.custom.requests")}</h3>
            <ul className="grid gap-2">
              {requests.data.map((r) => (
                <li key={r.id} className="grid gap-2 rounded-[var(--radius-lg)] border border-border p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Badge tone={tone(r.status)}>{t(`brand.custom.status.${r.status}` as MessageKey)}</Badge>
                    <span className="text-xs text-muted-foreground">{formatDateTime(locale, r.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-line">{r.brief}</p>
                  {r.staff_note ? <p className="text-muted-foreground">{t("brand.custom.note", { note: r.staff_note })}</p> : null}
                  {r.status === "open" || r.status === "in_progress" ? (
                    <div><Button size="sm" variant="ghost" loading={cancel.isPending && cancel.variables === r.id} onClick={() => cancel.mutate(r.id)}>{t("brand.custom.cancel")}</Button></div>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-2">
      <span className="text-sm font-medium leading-none">{label}</span>
      {children}
    </div>
  );
}

/** The portfolio header as patients will see it, with the chosen accent. */
function Preview({ practice, accent }: { practice: Practice; accent: string }) {
  const { apiBaseUrl } = useBrandex();
  const t = useT();
  const { profile } = practice;
  const hex = accentHex(accent);
  return (
    <div aria-label={t("brand.preview")} role="img" className="overflow-hidden rounded-[var(--radius-xl)] border border-border">
      <div className="flex items-center gap-4 p-5 text-white" style={{ background: `linear-gradient(120deg, ${hex}, ${hex}cc)` }}>
        {profile.photo ? (
          <img src={mediaUrl(apiBaseUrl, profile.photo.url)} alt="" className="h-16 w-16 rounded-full object-cover ring-2 ring-white/70" />
        ) : (
          <span className="grid h-16 w-16 place-items-center rounded-full bg-white/20 text-xl font-semibold">{profile.initials}</span>
        )}
        <span className="grid min-w-0 flex-1">
          <span className="truncate font-display text-xl font-semibold">{profile.full_name}</span>
          <span className="truncate text-sm opacity-90">{profile.designation}</span>
        </span>
        {profile.logo ? <img src={mediaUrl(apiBaseUrl, profile.logo.url)} alt="" className="h-12 w-auto max-w-28 rounded bg-white/90 object-contain p-1" /> : null}
      </div>
    </div>
  );
}

function ImageSlot({ orgId, practice, slot }: { orgId: string; practice: Practice; slot: Slot }) {
  const t = useT();
  const { api, apiBaseUrl } = useBrandex();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const [tooBig, setTooBig] = useState(false);
  const current = practice.profile[slot];
  const path = { params: { path: { org_id: orgId, slot } } };
  const done = (data: Practice | undefined) => {
    if (data !== undefined) qc.setQueryData(keys.practice(orgId), data);
  };
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { data } = await api.http.PUT("/v1/orgs/{org_id}/brand/images/{slot}", {
        ...path,
        // Raw bytes, not JSON: the server sniffs the real type.
        body: file as unknown as number[],
        bodySerializer: (b: unknown) => b as BodyInit,
        headers: { "content-type": file.type || "application/octet-stream" },
      });
      return data;
    },
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: async () => (await api.http.DELETE("/v1/orgs/{org_id}/brand/images/{slot}", path)).data,
    onSuccess: done,
  });
  const label = t(`brand.slot.${slot}` as MessageKey);
  return (
    <div className="grid content-start gap-3 rounded-[var(--radius-lg)] border border-border p-4">
      <p className="font-medium" id={`${id}-label`}>{label}</p>
      <div className="grid h-32 place-items-center overflow-hidden rounded-md bg-muted">
        {current ? (
          <img
            src={mediaUrl(apiBaseUrl, current.url)}
            alt={label}
            className={slot === "photo" ? "h-full w-full object-cover" : slot === "favicon" ? "h-16 w-16 rounded-md object-contain shadow" : "max-h-28 max-w-full object-contain p-2"}
          />
        ) : (
          <ImageUp className="h-8 w-8 text-muted-foreground" aria-hidden />
        )}
      </div>
      <p className="text-xs text-muted-foreground">{t(slot === "photo" ? "brand.photoHint" : slot === "favicon" ? "brand.faviconHint" : "brand.logoHint")}</p>
      <input
        ref={input}
        id={id}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        aria-labelledby={`${id}-label`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file === undefined) return;
          setTooBig(file.size > MAX_BYTES);
          if (file.size <= MAX_BYTES) upload.mutate(file);
        }}
      />
      {tooBig ? <Alert tone="error">{t("brand.tooBig")}</Alert> : null}
      {upload.isError ? <Alert tone="error">{errorMessage(upload.error, t)}</Alert> : null}
      {remove.isError ? <Alert tone="error">{errorMessage(remove.error, t)}</Alert> : null}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" loading={upload.isPending} onClick={() => input.current?.click()}>
          {t(current ? "brand.replace" : "brand.upload")}
        </Button>
        {current ? (
          <Button size="sm" variant="ghost" loading={remove.isPending} aria-label={t("brand.remove", { slot: label })} onClick={() => remove.mutate()}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
